/**
 * @file frontend/src/layouts/sidebarMatching.js
 * @module sidebar
 *
 * Pure functions that decide which sidebar group should auto-expand
 * given the current location, and whether a sub-item should be marked
 * "active". Extracted from `DashboardLayout.js` so they can be unit-
 * tested without rendering the entire layout (~1600 LOC).
 *
 * Why it matters
 * --------------
 * The Teacher-Portal-`/dashboard` regression (Feb 2026) lived inside
 * this exact decision: when a SUB-ITEM's href accidentally matches the
 * top-level Dashboard route, the auto-expand picks the wrong group. A
 * test against these pure functions would have caught the bug before
 * merge — see `sidebarMatching.test.js`.
 */

/**
 * @returns {boolean} `pathname` exactly matches `href`, or starts with `href + '/'`.
 *   Special-cased so a sidebar entry with `href === '/dashboard'` does
 *   NOT swallow `/dashboard/something` traffic that should belong to a
 *   nested route.
 */
export const isActive = (pathname, href) => {
  if (!href) return false;
  return pathname === href || pathname.startsWith(href + '/');
};

/**
 * Decide whether a parent nav `item` should be considered "active" for
 * the given `pathname`. A parent is active iff at least one of its
 * children (or grand-children, when a child is a category group) has
 * its href EXACTLY equal to the current pathname.
 *
 * @returns {boolean}
 */
export const isParentActive = (pathname, item) => {
  if (!item.children) return isActive(pathname, item.href);
  return item.children.some((child) => {
    if (child.isCategory && child.children) {
      return child.children.some((sub) => pathname === sub.href);
    }
    return pathname === child.href;
  });
};

/**
 * Find which group from `nav` should auto-expand for the given
 * `pathname`. Returns the matching item's `name`, or `null` if no group
 * should be expanded (e.g., on the global `/dashboard` route).
 *
 * @param {string} pathname
 * @param {Array<{name: string, children?: Array<unknown>}>} nav
 * @returns {string|null}
 */
export const findGroupToExpand = (pathname, nav) => {
  for (const item of nav || []) {
    if (item.children && isParentActive(pathname, item)) {
      return item.name;
    }
  }
  return null;
};
